import useSWR from 'swr';

type QueryOptions = {
  skip?: boolean;
  deps?: unknown[];
  key?: string;
  refreshInterval?: number;
};

export function useRestQuery<TData>(
  queryFn: () => Promise<TData>,
  options: QueryOptions = {}
) {
  const { skip = false, deps = [], key, refreshInterval } = options;

  // Stable cache key: explicit key + deps for cross-navigation caching
  const swrKey = skip ? null : key ? [key, ...deps] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<TData>(
    swrKey,
    () => queryFn(),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      shouldRetryOnError: true,
      errorRetryCount: 3,
      keepPreviousData: true,
      ...(refreshInterval ? { refreshInterval } : {}),
    }
  );

  return {
    data: data ?? null,
    loading: isLoading,
    isValidating,
    error: error ?? null,
    refetch: () => mutate(),
    updateCache: (updater: (current: TData) => TData) =>
      mutate((current) => (current ? updater(current) : current), { revalidate: false }),
  };
}
